import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetDefaultServiceLog, useServiceTypes } from "../../fetch/fetchMaintenance";
import { Button } from "@mui/material";
import { getAuthenticatedHeaders } from "../../fetch/fetchHeaders";
import AddEditServiceLogUI from "./addEditServiceLogUI";
import { useNotification } from "../../hooks";
import { msalInstance } from "../..";
import Spinner from "../spinner/Spinner";
import { logFeatureUsage } from "../../fetch/insightsHelper";

export default function AddServiceLog() {
    const { showErrorModal } = useNotification();
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const [notFound, setNotFound] = useState(false);
    const account = msalInstance.getActiveAccount();
    const userName = account?.name;

    useEffect(() => {
        logFeatureUsage('AddServiceLog');
        document.title = 'Add Service Log';
    }, []);

    const { data, isLoading } = useGetDefaultServiceLog(!notFound ? id : null);    
    const [model, setModel] = useState({ engines: [], technicianName: userName });
    const { serviceType } = model;

    useEffect(() => {
        if (data?.status === 404) {
            setNotFound(true);
        }
    }, [data?.status]);

    let impactedSerials = '';
    if (!!model.engines) {
        impactedSerials = model.engines.filter(e => e.impacted).map(e => e.engineSerial).join(', ');
    }

    const { data: serviceTypes } = useServiceTypes(impactedSerials);

    let inMercNet = false,
        locked = false;
    const matchingServiceTypes = serviceTypes.filter(it => it.value === serviceType);
    if (!!matchingServiceTypes && matchingServiceTypes.length > 0) {
        inMercNet = matchingServiceTypes[0].existsInMercNet;
        locked = matchingServiceTypes[0].locked;
    }

    const rawJson = useMemo(() => JSON.stringify(data), [data]);
    useEffect(() => {
        if (!!data) {
            setModel({ ...data, technicianName: !data.technicianName ? userName : data.technicianName });
        }
    }, [rawJson]);

    const handleBaseFieldValueChanged = (fieldName, fieldValue) => {
        if (fieldName == 'serviceType' && fieldValue?.length > 200) {
            fieldValue = fieldValue.substring(0, 200);
        }

        setModel((prevModel) => ({
            ...prevModel,
            [fieldName]: fieldValue,
        }));
    };

    const handleEngineFieldValueChanged = (index, fieldName, fieldValue) => {
        const newModel = { ...model };

        newModel.engines[index] = { ...model.engines[index] };
        newModel.engines[index][fieldName] = fieldValue;

        if (fieldName == 'engineLocation') {
            let cityId = 0;
            let diagnosticPort = 0;
            const canBus = 'CanP';

            switch (fieldValue) {
                case 'STBD Engine':
                    cityId = 11
                    break;
                case 'PORT Engine':
                    cityId = 12
                    break;
                case 'STBD Inner Engine':
                    cityId = 13
                    break;
                case 'PORT Inner Engine':
                    cityId = 14
                    break;
                case 'STBD Outer Engine':
                    cityId = 13
                    diagnosticPort = 1;
                    break;
                case 'PORT Outer Engine':
                    cityId = 14
                    diagnosticPort = 1;
                    break;
            }
            newModel.engines[index].cityId = cityId;
            newModel.engines[index].diagnosticPort = diagnosticPort;
            newModel.engines[index].canBus = canBus;
        }

        if (fieldName == 'engineHours' && fieldValue == '') {
            newModel.engines[index][fieldName] = null;
        }

        setModel(newModel);
    };

    const handleAddEngineClick = () => {
        const newEngine = {
            "serviceLogId": null,
            "diagnosticPort": 0,
            "cityId": 0,
            "canBus": 'CanP',
            "moduleSerialNumber": null,
            "engineLocation": "",
            "engineSerial": "",
            "engineHours": null,
            "comments": "",
            "impacted": true
        };

        setModel((prevModel) => ({
            ...prevModel,
            engines: [...prevModel.engines, newEngine],
        }));
    }

    const handleRemoveEngineClick = (index) => {
        const newModel = { ...model };
        newModel.engines = newModel.engines.filter((_, i) => i !== index);
        setModel(newModel);
    };

    const handleSave = () => {
        setSaving(true);
        return getAuthenticatedHeaders({ 'Content-Type': 'application/json' }).then((headers) => {
            return fetch('/api/v1/servicelog/item', { method: 'put', headers, body: JSON.stringify(model) })
                .then(response => {
                    if (response.status < 400) {
                        navigate('/literature/service/log');
                    }
                    else {
                        showErrorModal("Failed to save service log.");
                        setSaving(false);
                    }
                });
        });
    }

    const invalidEngines = (model?.engines || []).some(engine => !engine.engineLocation || !engine.engineSerial);

    const isSaveDisabled = isLoading || !model.dateOfService || !model.serviceType || invalidEngines || saving;

    if (isLoading) {
        return <div style={{ position: 'relative', left: 'calc(50% - 30px)' }}><Spinner /></div>;
    }

    return (

        <AddEditServiceLogUI
            model={model}
            handleBaseFieldValueChanged={handleBaseFieldValueChanged}
            handleEngineFieldValueChanged={handleEngineFieldValueChanged}
            handleAddEngineClick={handleAddEngineClick}
            handleRemoveEngineClick={handleRemoveEngineClick}
            isEdit
            inMercNet={inMercNet}
            locked={locked}
            isAdd
            notFound={notFound}
        >
            <Button onClick={handleSave} disabled={isSaveDisabled || locked}>
                {!!model.id ? 'Update' : 'Save'}
                {saving ? (
                    <>
                        &nbsp;
                        <Spinner width={16} displayInline />
                    </>
                ) : null}
            </Button>
        </AddEditServiceLogUI>
    )
}